import { Mail, RefreshCw, RotateCcw, Send } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  CardSubtitle,
  CardTitle,
  EmptyState,
  FormField,
  Input,
  Select,
  Skeleton,
  Textarea,
} from '@/components/ui';
import { useNotify } from '@/hooks/useNotify';
import { useI18n } from '@/i18n';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';

type MailTemplateInfo = Awaited<ReturnType<typeof api.admin.mailTemplates>>['templates'][number];

/**
 * Editor für die Mailvorlagen.
 *
 * Betreff und Fließtext lassen sich je Sprache überschreiben; Platzhalter in
 * geschweiften Klammern werden beim Versand ersetzt. Kästen, Schaltflächen und
 * Links bleiben fest - ein Überschreiben kann eine Mail also nie funktional
 * kaputt machen. Leere Felder fallen auf den Standardtext zurück.
 */
export function AdminMailPage() {
  const { t } = useI18n();
  const notify = useNotify();

  const [templates, setTemplates] = useState<MailTemplateInfo[]>([]);
  const [mailEnabled, setMailEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState('');
  const [locale, setLocale] = useState<'de' | 'en'>('de');

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api.admin.mailTemplates();
      setTemplates(data.templates);
      setMailEnabled(data.mailEnabled);
      setKind((current) => current || data.templates[0]?.kind || '');
    } catch (cause) {
      notify.fromError(cause, t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [notify, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = useMemo(
    () => templates.find((entry) => entry.kind === kind) ?? null,
    [templates, kind],
  );

  // Beim Wechsel von Vorlage oder Sprache die Felder fuellen: Ueberschreibung,
  // sonst Standardtext.
  useEffect(() => {
    if (!selected) return;
    const override = selected.overrides[locale];
    setSubject(override?.subject ?? selected.defaults[locale].subject);
    setBody(override?.body ?? selected.defaults[locale].body);
  }, [selected, locale]);

  const isOverridden = Boolean(
    selected && (selected.overrides[locale]?.subject || selected.overrides[locale]?.body),
  );

  const save = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const defaults = selected.defaults[locale];
      // Unveraenderte Felder nicht als Ueberschreibung speichern.
      await api.admin.saveMailTemplate(selected.kind, locale, {
        subject: subject.trim() === defaults.subject ? null : subject.trim(),
        body: body.trim() === defaults.body.trim() ? null : body,
      });
      notify.success(t('mailAdmin.saved'));
      await load();
    } catch (cause) {
      notify.fromError(cause, t('errors.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.admin.saveMailTemplate(selected.kind, locale, { subject: null, body: null });
      notify.success(t('mailAdmin.resetDone'));
      await load();
    } catch (cause) {
      notify.fromError(cause, t('errors.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const result = await api.admin.testMailTemplate(selected.kind, locale);
      notify.success(t('mailAdmin.testSent', { to: result.to }));
    } catch (cause) {
      notify.fromError(cause, t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Skeleton className="h-64" />;

  return (
    <div>
      <div className="mb-4 flex items-start gap-2">
        <Mail size={18} className="mt-0.5 text-t2" />
        <div>
          <h2 className="text-[16px] font-bold text-t0">{t('mailAdmin.title')}</h2>
          <p className="text-[13px] text-t1">{t('mailAdmin.subtitle')}</p>
        </div>
      </div>

      {!mailEnabled && (
        <div className="mb-4 rounded-[var(--radius-vela-md)] border border-warn/30 bg-warn-soft px-4 py-3 text-[13px] text-t0">
          {t('mailAdmin.smtpMissing')}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,280px)_1fr]">
        <Card>
          <FormField label={t('mailAdmin.template')}>
            <Select value={kind} onChange={(event) => setKind(event.target.value)}>
              {templates.map((entry) => (
                <option key={entry.kind} value={entry.kind}>
                  {t(`mailAdmin.kind_${entry.kind}` as never) || entry.kind}
                </option>
              ))}
            </Select>
          </FormField>

          <div className="mt-3 flex gap-2">
            {(['de', 'en'] as const).map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => setLocale(code)}
                className={`flex-1 rounded-[var(--radius-vela-sm)] border px-3 py-2 text-xs font-semibold uppercase transition ${
                  locale === code
                    ? 'border-acc bg-acc-soft text-acc'
                    : 'border-line bg-bg-3 text-t1 hover:border-line-2'
                }`}
              >
                {code}
              </button>
            ))}
          </div>

          {selected && (
            <div className="mt-4">
              <p className="mb-1.5 text-[12px] font-semibold text-t1">
                {t('mailAdmin.placeholders')}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {selected.placeholders.map((placeholder) => (
                  <code
                    key={placeholder}
                    className="rounded bg-bg-3 px-1.5 py-0.5 font-mono text-[11px] text-t1"
                  >
                    {placeholder}
                  </code>
                ))}
              </div>
              <p className="mt-3 text-[12px] leading-relaxed text-t2">{t('mailAdmin.fixedHint')}</p>
            </div>
          )}
        </Card>

        {selected && (
          <Card>
            <CardHeader>
              <div>
                <CardTitle className="flex items-center gap-2">
                  {t(`mailAdmin.kind_${selected.kind}` as never) || selected.kind}
                  {isOverridden && <Badge variant="accent">{t('mailAdmin.overridden')}</Badge>}
                </CardTitle>
                <CardSubtitle>{t('mailAdmin.editHint')}</CardSubtitle>
              </div>
            </CardHeader>

            <div className="space-y-4">
              <FormField label={t('mailAdmin.subject')}>
                <Input value={subject} onChange={(event) => setSubject(event.target.value)} />
              </FormField>

              <FormField label={t('mailAdmin.body')} hint={t('mailAdmin.bodyHint')}>
                <Textarea
                  rows={10}
                  className="font-mono text-[12.5px] leading-relaxed"
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                />
              </FormField>

              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  variant="ghost"
                  icon={<RotateCcw size={14} />}
                  onClick={() => void reset()}
                  disabled={busy || !isOverridden}
                >
                  {t('mailAdmin.reset')}
                </Button>
                <Button
                  variant="secondary"
                  icon={<Send size={14} />}
                  onClick={() => void sendTest()}
                  disabled={busy || !mailEnabled}
                >
                  {t('mailAdmin.sendTest')}
                </Button>
                <Button onClick={() => void save()} disabled={busy}>
                  {t('common.save')}
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>

      <div className="mt-4">
        <MailLogSection />
      </div>
    </div>
  );
}

type MailLogEntry = Awaited<ReturnType<typeof api.admin.mailLog>>['entries'][number];

const LOG_STATUS_VARIANT = {
  SENT: 'success',
  FAILED: 'danger',
  DISABLED: 'neutral',
} as const;

/**
 * Versandprotokoll: jede Mail mit Zeitpunkt, Vorlage, Empfaenger und Status.
 * Bewusst ohne Inhalt - Reset- und Bestaetigungslinks gehoeren nicht in die
 * Datenbank. Zum erneuten Verschicken eines Bestaetigungslinks dient der Knopf
 * im Benutzer-Dialog, der ein frisches Token erzeugt.
 */
function MailLogSection() {
  const { t } = useI18n();
  const notify = useNotify();
  const [entries, setEntries] = useState<MailLogEntry[]>([]);
  const [filter, setFilter] = useState<'' | 'SENT' | 'FAILED' | 'DISABLED'>('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.admin.mailLog(filter || undefined);
      setEntries(data.entries);
    } catch (cause) {
      notify.fromError(cause, t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [filter, notify, t]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Card padding="none">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4">
        <div>
          <CardTitle>{t('mailAdmin.logTitle')}</CardTitle>
          <CardSubtitle>{t('mailAdmin.logHint')}</CardSubtitle>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={filter}
            onChange={(event) => setFilter(event.target.value as typeof filter)}
            className="w-40"
          >
            <option value="">{t('mailAdmin.logAll')}</option>
            <option value="SENT">{t('mailAdmin.logSent')}</option>
            <option value="FAILED">{t('mailAdmin.logFailed')}</option>
            <option value="DISABLED">{t('mailAdmin.logDisabled')}</option>
          </Select>
          <Button size="sm" variant="ghost" icon={<RefreshCw size={13} />} onClick={() => void load()}>
            {t('common.retry')}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-5">
          <Skeleton className="h-24" />
        </div>
      ) : entries.length === 0 ? (
        <div className="p-6">
          <EmptyState icon="📭" title={t('mailAdmin.logEmpty')} />
        </div>
      ) : (
        <div className="max-h-[420px] overflow-auto">
          <table className="w-full text-[13px]">
            <tbody className="divide-y divide-line">
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td className="whitespace-nowrap px-5 py-2.5 text-t2">{formatDateTime(entry.createdAt)}</td>
                  <td className="px-5 py-2.5">
                    <p className="text-t0">{entry.email}</p>
                    <p className="text-[11.5px] text-t2">{entry.subject}</p>
                  </td>
                  <td className="px-5 py-2.5">
                    <Badge variant={LOG_STATUS_VARIANT[entry.status]}>
                      {entry.status === 'SENT'
                        ? t('mailAdmin.logSent')
                        : entry.status === 'FAILED'
                          ? t('mailAdmin.logFailed')
                          : t('mailAdmin.logDisabled')}
                    </Badge>
                    {entry.error && (
                      <p className="mt-1 max-w-[320px] truncate text-[11.5px] text-bad" title={entry.error}>
                        {entry.error}
                      </p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
